if (!customElements.get('hp-iphone-purchase-summary')) {
  customElements.define('hp-iphone-purchase-summary', class extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-hp-iphone]');
      if (!this.section || this.observer) return;
      this.config = this.readJson(this.querySelector('[data-hp-purchase-config]'));
      if (!this.config) return;
      this.variant = this.config.variant;
      this.quantityBlocked = this.config.quantityBlocked;
      this.pending = false;
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'it', {
        style: 'currency', currency: this.config.currency || 'EUR'
      });
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };
      this.onChange = (event) => {
        if (event.target.closest('variant-selects')) this.pending = true;
        if (event.target.matches('input[name="id"]') && event.target.form === this.form) {
          this.pending = false;
          this.quantityBlocked = false;
        }
        this.schedule();
      };
      this.onClick = (event) => {
        const detailsLink = event.target.closest?.('[data-hp-open-details]');
        if (detailsLink && this.section.contains(detailsLink)) {
          const details = this.section.querySelector('.hp-iphone-details');
          if (details) details.open = true;
        }
        this.guardPurchase(event);
      };
      this.onInput = (event) => {
        if (event.target.matches('[data-hp-device-model]')) event.target.setCustomValidity('');
        if (event.target.name === 'quantity') this.schedule();
      };
      this.onSubmit = (event) => this.guardPurchase(event);
      this.onMobileBuy = () => this.mobileBuy();
      this.section.addEventListener('change', this.onChange, true);
      this.section.addEventListener('input', this.onInput, true);
      window.addEventListener('click', this.onClick, true);
      window.addEventListener('submit', this.onSubmit, true);
      this.querySelector('[data-hp-mobile-buy]').addEventListener('click', this.onMobileBuy);
      this.observer = new MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target))) this.schedule();
      });
      this.observer.observe(this.section, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['checked', 'data-amount', 'disabled', 'aria-disabled', 'class', 'value']
      });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          const sectionId = this.section.dataset.originalSection || this.dataset.sectionId;
          if (String(data.sectionId) !== String(sectionId)) return;
          this.variant = data.variant;
          this.quantityBlocked = data.html?.getElementById(`ProductSubmitButton-${sectionId}`)?.disabled === true;
          this.pending = false;
          this.schedule();
        });
      }
      this.update();
      this.querySelector('.hp-iphone-mobile-bar').hidden = false;
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
      this.unsubscribe?.();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.section?.removeEventListener('change', this.onChange, true);
      this.section?.removeEventListener('input', this.onInput, true);
      window.removeEventListener('click', this.onClick, true);
      window.removeEventListener('submit', this.onSubmit, true);
      this.querySelector('[data-hp-mobile-buy]')?.removeEventListener('click', this.onMobileBuy);
    }

    get form() {
      return this.section?.querySelector(`form[id="${CSS.escape(this.dataset.formId)}"]`);
    }

    get nativeButton() {
      return this.form?.querySelector('[name="add"], .product-form__submit');
    }

    get quantity() {
      const input = this.form?.elements.namedItem('quantity');
      const value = Number(input?.value || 1);
      return Number.isSafeInteger(value) && value > 0 ? value : null;
    }

    validateFields() {
      const model = this.section.querySelector('[data-hp-device-model]');
      if (model && model.form === this.form) {
        model.setCustomValidity(model.value.trim() ? '' : model.dataset.hpDeviceError);
        if (!model.reportValidity()) {
          model.scrollIntoView({ behavior: this.scrollBehavior(), block: 'center' });
          model.focus({ preventScroll: true });
          return false;
        }
      }
      const quantity = this.form?.elements.namedItem('quantity');
      return !quantity || quantity.reportValidity();
    }

    readJson(element) {
      try { return JSON.parse(element?.textContent || 'null'); } catch { return null; }
    }

    readVariant() {
      const id = this.form?.querySelector('input[name="id"]')?.value;
      if (!id) return null;
      const selected = this.readJson(this.section.querySelector('variant-selects [data-selected-variant]'));
      if (selected && String(selected.id) === id) return selected;
      return this.variant && String(this.variant.id) === id ? this.variant : null;
    }

    cents(value) {
      if (typeof value !== 'string' || !/^\d+(?:\.\d{1,2})?$/.test(value.trim())) return null;
      const amount = Math.round(Number(value) * 100);
      return Number.isSafeInteger(amount) ? amount : null;
    }

    readExtras() {
      const result = { battery: 0, other: 0, hasBattery: false, hasOther: false, selected: false, known: true };
      this.section.querySelectorAll('.lb-upsell-ppa .ppa-prod-container').forEach((item) => {
        const checkbox = item.querySelector('.ppa-add-checkbox-input');
        const title = item.querySelector('.ppa-prod-details-title')?.textContent?.trim() || '';
        if (checkbox && title && !checkbox.hasAttribute('aria-label') && !checkbox.hasAttribute('aria-labelledby')) {
          if (!Array.from(checkbox.labels || []).some((label) => label.textContent.trim())) {
            checkbox.setAttribute('aria-label', title);
          }
        }
        if (!checkbox?.checked) return;
        result.selected = true;
        const battery = /batter(?:ia|y)/i.test(title);
        result.hasBattery ||= battery;
        result.hasOther ||= !battery;
        const amount = this.cents(item.querySelector('.ppa-prod-details-price[data-amount]')?.dataset.amount);
        if (amount === null || this.config.currency !== 'EUR') result.known = false;
        else result[battery ? 'battery' : 'other'] += amount;
      });
      return result;
    }

    state(variant) {
      if (this.pending) return 'loading';
      if (!variant) return 'unavailable';
      return variant.available && !this.quantityBlocked ? 'available' : 'soldout';
    }

    busy() {
      const original = this.form?.closest('product-form')?.submitButton;
      return [this.nativeButton, original].filter(Boolean).some((button) =>
        button.disabled || button.getAttribute('aria-disabled') === 'true' || button.classList.contains('loading')
      );
    }

    text(selector, value) {
      const node = this.querySelector(selector);
      if (node && node.textContent !== value) node.textContent = value;
    }

    update() {
      if (!this.isConnected) return;
      this.variant = this.readVariant();
      const state = this.state(this.variant);
      const extras = this.readExtras();
      const phone = Number(this.variant?.price);
      const quantity = this.quantity;
      const known = this.variant && Number.isSafeInteger(phone) && extras.known && quantity !== null && (!extras.selected || quantity === 1);
      const total = known ? phone * quantity + extras.battery + extras.other : null;
      const strings = this.config.strings;
      const totalText = !this.variant ? strings.choose : total === null ? strings.cartTotal : this.money.format(total / 100);
      this.section.dataset.hpPurchaseState = state;
      this.section.dataset.hpBatterySelected = String(extras.hasBattery);
      this.section.dataset.hpAddonsSelected = String(extras.selected);
      this.text('[data-hp-phone-price]', this.variant && quantity ? this.money.format(phone * quantity / 100) : strings.choose);
      this.text('[data-hp-quantity]', quantity && quantity > 1 ? `× ${quantity}` : '');
      this.text('[data-hp-total]', state === 'loading' ? strings.loading : totalText);
      this.text('[data-hp-mobile-total]', state === 'loading' ? strings.loading : totalText);
      this.text('[data-hp-battery-price]', extras.known ? this.money.format(extras.battery / 100) : strings.cartTotal);
      this.text('[data-hp-extras-price]', extras.known ? this.money.format(extras.other / 100) : strings.cartTotal);
      this.querySelector('[data-hp-battery-row]').hidden = !extras.hasBattery;
      this.querySelector('[data-hp-extras-row]').hidden = !extras.hasOther;
      const installments = this.querySelector('[data-hp-installments]');
      installments.hidden = true;
      this.dataset.hpKlarnaTotal = total === null ? '' : String(total);
      this.dataset.hpKlarnaAvailable = String(state === 'available');
      if (total !== null) this.text('[data-hp-installments]', strings.installments.replace('__amount__', this.money.format(Math.round(total / 3) / 100)));
      this.querySelector('.hp-iphone-purchase__status').hidden = state === 'available' || state === 'loading';
      this.text('[data-hp-status]', state === 'soldout' ? strings.soldOut : strings.unavailable);
      const mobileButton = this.querySelector('[data-hp-mobile-buy]');
      mobileButton.disabled = state === 'loading' || state === 'available' && (!this.nativeButton || this.busy());
      this.text('[data-hp-mobile-buy]', state === 'loading' ? strings.loading : state === 'soldout' ? strings.chooseAlternative : state === 'unavailable' ? strings.choose : strings.add);
    }

    guardPurchase(event) {
      const form = this.form;
      if (!form) return;
      const control = event.target.closest?.('button, input[type="submit"], [role="button"], .product-form__submit, .shopify-payment-button, shopify-accelerated-checkout');
      const productForm = form.closest('product-form');
      const payment = event.target.closest?.('.shopify-payment-button, shopify-accelerated-checkout');
      const purchase = event.type === 'submit' ? event.target === form : control && (form.contains(control) || productForm?.contains(control));
      if (!purchase) return;
      const unavailable = this.state(this.readVariant()) !== 'available';
      const invalidFields = !unavailable && !this.validateFields();
      const blockedPayment = payment && (this.readExtras().selected || this.section.querySelector('[data-hp-device-model]'));
      if (!unavailable && !invalidFields && !blockedPayment) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.schedule();
    }

    mobileBuy() {
      this.update();
      const state = this.state(this.variant);
      if (state === 'loading') return;
      if (state !== 'available') {
        const options = this.section.querySelector('variant-selects');
        const target = options || this.querySelector('.hp-iphone-purchase__status');
        target?.scrollIntoView({ behavior: this.scrollBehavior(), block: 'center' });
        target?.querySelector('input:checked, select, input:not([type="hidden"]), a')?.focus({ preventScroll: true });
        return;
      }
      if (this.busy()) return;
      const button = this.nativeButton;
      if (button?.getClientRects().length) button.click();
      else this.form?.scrollIntoView({ behavior: this.scrollBehavior(), block: 'center' });
    }

    scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }
  });
}
